import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { history } from '../../utils';
import { SearchChip } from '../../ts/interface/components/table.interface';
import { ISortConfig } from '../../ts/interface/components/redesing/table.interface';
import {
  useTransactionsRelayQuery,
  TransactionQueryFilter,
  TransactionSortInput,
} from '../../hooks/transaction/query/V2/useTransactionsRelay';
import { mapSystemTransactionsToUnified } from '../../hooks/transaction/query/V2/systemTransactionMappers';
import { useUpdateEffect } from '../../hooks/use-update-effect';
import { PaginationTable } from '../../components/Redesing/table/pagination-table';
import ShareIcon from '@mui/icons-material/Share';
import { columns } from '../../tableColumns/transactionColumns';
import Card from '@mui/material/Card';
import { getAllStripeCharges, getAllStripeTransfers } from '../../api/system';
import { MovaticFieldGroupDate } from '../../components/Redesing/movatic-field-group';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants';
import CustomAsyncToast from '../../components/Redesing/custom-async-toast';
import CustomToast from '../../components/Redesing/custom-toast';
import useUrlState from '../../hooks/use-url-state';
import useSearchDebounce from '../../hooks/search/useSearchDebounce';
import { getDateAtLastSecond } from '../../helpers/dateHelpers';
import { useSystemStore } from '../../store/systemStore';
import { matchesTransactionSearch } from './transactionSearch';

const COLUMN_TO_SORT_FIELD: Record<string, string> = {
  created: 'CREATED',
  amount: 'AMOUNT',
  type: 'TYPE',
  status: 'STATUS',
  description: 'DESCRIPTION',
};

interface TransactionsProps {
  system?: {
    hideExportPayments?: boolean;
    hideExportTransfers?: boolean;
  };
  onTotalCountChange?: (count: number | undefined) => void;
}

const Transactions = ({ system, onTotalCountChange }: TransactionsProps) => {
  const { systemInfo } = useSystemStore();
  const [chips, setChips] = useState<SearchChip[]>([]);
  const [preSelectedStartDate, setPreSelectedStartDate] = useState('');
  const [preSelectedEndDate, setPreSelectedEndDate] = useState('');
  // Not in URL state: pages are reached through opaque keyset cursors, so a
  // cold load can't restore an arbitrary page index.
  const [pageNumber, setPageNumber] = useState(0);

  const [pageState, setPageState, isComplete, isRestoring] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    search: '',
    startDate: '',
    endDate: '',
    sortConfig: {
      key: 'created',
      direction: 'desc',
    } as ISortConfig,
  });

  useUpdateEffect(() => {
    if (isRestoring && isComplete) {
      setPreSelectedStartDate(pageState.startDate);
      setPreSelectedEndDate(pageState.endDate);
      if (pageState.startDate) {
        setChips((prevChips) => {
          const chip: SearchChip = {
            label: 'Start Date',
            field: 'startDate',
            value: new Date(pageState.startDate),
            displayValue: new Date(pageState.startDate).toLocaleDateString(),
          };
          return [...prevChips.filter((chip) => chip.field !== 'startDate'), chip];
        });
      }
      if (pageState.endDate) {
        setChips((prevChips) => {
          const chip: SearchChip = {
            label: 'End Date',
            field: 'endDate',
            value: new Date(pageState.endDate),
            displayValue: new Date(pageState.endDate).toLocaleDateString(),
          };
          return [...prevChips.filter((chip) => chip.field !== 'endDate'), chip];
        });
      }
    }
  }, [isRestoring]);

  const debouncedSearch = useSearchDebounce(pageState.search);
  const searchTerm = (debouncedSearch ?? '').trim();

  const filter = useMemo<TransactionQueryFilter>(() => {
    const f: TransactionQueryFilter = { systemId: systemInfo.gid };
    if (pageState.startDate) {
      f.createdAfter = new Date(pageState.startDate).toISOString();
    }
    if (pageState.endDate) {
      f.createdBefore = getDateAtLastSecond(new Date(pageState.endDate)).toISOString();
    }
    return f;
  }, [systemInfo.gid, pageState.startDate, pageState.endDate]);

  const sort = useMemo<TransactionSortInput[]>(() => {
    const field = COLUMN_TO_SORT_FIELD[pageState.sortConfig.key] ?? 'CREATED';
    return [
      {
        field,
        order: pageState.sortConfig.direction === 'asc' ? 'ASC' : 'DESC',
      } as TransactionSortInput,
    ];
  }, [pageState.sortConfig]);

  // One page per fetch, sized to the table. The merged Transaction resolver
  // can't search server-side (no filter reaches the user relationship), so an
  // active search term switches the hook to fetchAll and the term is matched
  // client-side over the drained list.
  const { data, count, isLoading, isRefetching, loadNext, hasNextPage, error } =
    useTransactionsRelayQuery(filter, {
      limit: pageState.sizePerPage,
      sort,
      enabled: Boolean(systemInfo.gid),
      fetchAll: Boolean(searchTerm),
    });

  const mapped = useMemo(() => mapSystemTransactionsToUnified(data ?? []), [data]);
  const visibleTransactions = useMemo(
    () =>
      searchTerm
        ? mapped.filter((transaction) =>
            matchesTransactionSearch(transaction, searchTerm)
          )
        : mapped,
    [mapped, searchTerm]
  );

  useEffect(() => {
    onTotalCountChange?.(count);
  }, [count, onTotalCountChange]);

  useEffect(() => {
    if (error) {
      CustomToast({ type: 'error', message: 'Error loading transactions' });
    }
  }, [error]);

  // Fetch until the current page is covered. Advancing the page and fetching
  // are decoupled so two quick Next clicks (or a dropped request) can't leave
  // the page index pointing past the loaded rows; loadNext ignores calls
  // while a page is already in flight, and this re-runs as each page lands.
  useEffect(() => {
    if (searchTerm || !hasNextPage || pageNumber === 0) return;
    if (mapped.length && mapped.length < (pageNumber + 1) * pageState.sizePerPage) {
      loadNext(pageState.sizePerPage);
    }
  }, [
    searchTerm,
    hasNextPage,
    pageNumber,
    mapped.length,
    pageState.sizePerPage,
    loadNext,
  ]);

  const handleChipDelete = useCallback(
    (deletedGroup: SearchChip) => {
      setPageNumber(0);
      if (deletedGroup.label === 'Start Date') {
        setPageState((prev) => ({
          ...prev,
          startDate: '',
        }));
        setPreSelectedStartDate('');
      }
      if (deletedGroup.label === 'End Date') {
        setPageState((prev) => ({
          ...prev,
          endDate: '',
        }));
        setPreSelectedEndDate('');
      }
      setChips((prevChips) => {
        return prevChips.filter((group) => {
          return group.label !== deletedGroup.label;
        });
      });
    },
    [setPageState]
  );

  const handleDateChangeNew = useCallback(
    (field: 'startDate' | 'endDate', date: Date | null): void => {
      if (date) {
        // @ts-ignore
        setChips((prevChips) => {
          const chip: SearchChip = {
            label: field === 'startDate' ? 'Start Date' : 'End Date',
            field: field,
            value: date,
            displayValue: date.toLocaleDateString(),
          };

          return [...prevChips.filter((chip) => chip.field !== field), chip];
        });
      }
    },
    []
  );

  const rowClick = (rowId: string) => {
    history.push(`/billing/transactions/detail/${rowId}`);
  };

  const generateStripeTransfersCSV = () => {
    const startDateFromPageState = pageState?.startDate;
    const endDateFromPageState = pageState?.endDate;
    const startDate = startDateFromPageState && new Date(startDateFromPageState);
    const endDate =
      endDateFromPageState && getDateAtLastSecond(new Date(endDateFromPageState));
    let formattedStartDate = '';
    let formattedEndDate = '';
    if (startDate && endDate) {
      formattedStartDate = startDate.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
      formatEndDate(endDate);
      formattedEndDate = endDate.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    }
    CustomAsyncToast({
      promise: () => getAllStripeTransfers(formattedStartDate, formattedEndDate),
      successMessage: () =>
        'The csv file is now being compiled. It may take a few minutes to be delivered!',
      errorMessage: 'Error generating CSV',
      loadingMessage: 'Starting to Generate CSV...',
    });
  };

  const generateStripePaymentCSV = () => {
    // Rows are cursor-paged, so gate on the server's total for the applied
    // date range (all transaction types, so this can only over-count charges).
    if ((count ?? 0) > 3000) {
      return CustomToast({
        type: 'error',
        message:
          'Attempting to download more than 3000 records. Please filter your results further and try again.',
      });
    }
    const startDateFromPageState = pageState?.startDate;
    const endDateFromPageState = pageState?.endDate;
    const startDate = startDateFromPageState && new Date(startDateFromPageState);
    const endDate =
      endDateFromPageState && getDateAtLastSecond(new Date(endDateFromPageState));
    let formattedStartDate = '';
    let formattedEndDate = '';
    if (startDate && endDate) {
      formattedStartDate = startDate.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
      formatEndDate(endDate);
      formattedEndDate = endDate.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    }
    CustomAsyncToast({
      promise: () => getAllStripeCharges(formattedStartDate, formattedEndDate),
      successMessage: () =>
        'The csv file is now being compiled. It may take a few minutes to be delivered!',
      errorMessage: 'Error generating CSV',
      loadingMessage: 'Starting to Generate CSV...',
    });
  };

  const formatEndDate = (eDate: Date | null) => {
    if (eDate != null) {
      eDate.setDate(eDate.getDate() + 1);
    }
  };

  const { hideExportPayments, hideExportTransfers } = system || {};
  return (
    <Card>
      <PaginationTable
        dataId={'transactions'}
        localSearch={false}
        hasMore={hasNextPage}
        disableHasMore={Boolean(searchTerm)}
        isLoading={
          isLoading ||
          (!searchTerm &&
            !error &&
            hasNextPage &&
            pageNumber > 0 &&
            mapped.length <= pageNumber * pageState.sizePerPage)
        }
        isLoadingCache={isRefetching}
        onRowCLick={rowClick}
        onSortChange={(sort) => {
          setPageNumber(0);
          setPageState((prev) => ({ ...prev, sortConfig: sort as ISortConfig }));
        }}
        sortBy={pageState.sortConfig.key}
        sortDirection={pageState.sortConfig.direction}
        padding={{ pt: 1, pl: 0, pr: 1 }}
        chips={chips}
        showFilter
        handleChipDelete={handleChipDelete}
        onApplyFilter={() => {
          let applyedFilter = false;
          if (preSelectedStartDate) {
            handleDateChangeNew('startDate', new Date(preSelectedStartDate));
            setPageState((prev) => ({ ...prev, startDate: preSelectedStartDate }));
            applyedFilter = true;
          }
          if (preSelectedEndDate) {
            handleDateChangeNew('endDate', new Date(preSelectedEndDate));
            setPageState((prev) => ({ ...prev, endDate: preSelectedEndDate }));
            applyedFilter = true;
          }
          if (applyedFilter) {
            setPageNumber(0);
          } else {
            setChips([]);
          }
        }}
        spaceFilters={
          <>
            <MovaticFieldGroupDate
              label="Start Date"
              value={new Date(preSelectedStartDate) || null}
              onChange={(date) => setPreSelectedStartDate(date)}
            />
            <MovaticFieldGroupDate
              label="End Date"
              value={new Date(preSelectedEndDate) || null}
              onChange={(date) => setPreSelectedEndDate(date)}
            />
          </>
        }
        columns={columns}
        buttons={[
          {
            startIcon: <ShareIcon />,
            variant: 'text',
            label: 'Export Transfers',
            onClick: generateStripeTransfersCSV,
            disabled: hideExportTransfers,
          },
          {
            startIcon: <ShareIcon />,
            variant: 'text',
            label: 'Export Payments',
            onClick: generateStripePaymentCSV,
            disabled: hideExportPayments,
          },
        ]}
        items={visibleTransactions}
        totalItems={searchTerm ? visibleTransactions.length : count}
        searchText={pageState.search}
        handleSearch={(search) => {
          setPageNumber(0);
          setPageState((prev) => ({ ...prev, search: search.target.value }));
        }}
        searchPlaceholder={'Search transactions'}
        page={pageNumber}
        rowsPerPage={pageState.sizePerPage}
        onPageChange={(page, newPage) => setPageNumber(newPage)}
        onRowsPerPageChange={(pageSizeString) => {
          setPageNumber(0);
          setPageState((prev) => ({
            ...prev,
            sizePerPage: Number(pageSizeString.target.value),
          }));
        }}
      />
    </Card>
  );
};

export default Transactions;
